/* Platform admins — the named users, and the log across every salon.
 *
 * Not a tenant page: it belongs to the product rather than to a customer, so it
 * does not register with nav.js and is reached from the top bar instead.
 *
 * `platform_admins` has a read policy and no write policy at all, deliberately —
 * see 10-platform-admins.sql. Adding and removing an admin is the one action
 * whose entire purpose is to widen access to every salon's data, so it goes
 * through the edge function where it is logged, or through the SQL editor. A
 * browser session that could grant itself company is a browser session left open
 * on a laptop that could.
 *
 * Two rules the function enforces and this page only echoes: an admin cannot
 * remove themselves, and the last admin cannot be removed. Both produce the same
 * locked-out database, and the second is the one somebody actually does — tidying
 * a list they are standing in.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { supportSession } from '../rules/support-session.js';
import { ConfirmButton } from '../components/confirm-button.js';

export const Admins = {
  name: 'AdminAdmins',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var admins = Vue.ref([]);
    var log = Vue.ref([]);
    var sessions = Vue.ref([]);
    var loaded = Vue.ref(false);
    var email = Vue.ref('');
    var note = Vue.ref('');

    async function load() {
      loaded.value = false;
      var rows = await api.query('Loading admins', function (sb) {
        return sb.from('platform_admins').select('*').order('email');
      });
      if (rows) admins.value = rows;

      // Every live support session, not only this admin's. "Who is in this
      // customer's console right now" has one honest answer.
      var live = await store.loadAllSupport();
      if (live) sessions.value = live;

      // The whole log, not one salon's — this is the only place it can be read
      // across tenants, and "what has been happening" is a question about the
      // product.
      var entries = await api.query('Loading log', function (sb) {
        return sb.from('admin_audit').select('*').order('at', { ascending: false }).limit(60);
      });
      if (entries) log.value = entries;
      loaded.value = true;
    }

    Vue.onMounted(load);

    async function add() {
      var value = email.value.trim();
      if (!value) return;
      var res = await api.call('admins.add', { email: value, note: note.value.trim() || null });
      if (!res) return;
      email.value = '';
      note.value = '';
      api.toast(value + ' can now administer every salon on this project.');
      load();
    }

    async function remove(row) {
      var res = await api.call('admins.remove', { userId: row.user_id });
      if (!res) return;
      api.toast('Removed. They keep their account and lose every salon.');
      load();
    }

    var tenantName = Vue.computed(function () {
      var out = {};
      store.tenants.value.forEach(function (t) { out[t.id] = t.name; });
      return out;
    });

    var emailOf = Vue.computed(function () {
      var out = {};
      admins.value.forEach(function (a) { out[a.user_id] = a.email; });
      return out;
    });

    var liveSessions = Vue.computed(function () {
      return sessions.value.map(function (s) {
        return {
          userId: s.user_id,
          email: emailOf.value[s.user_id] || s.user_id,
          tenantId: s.tenant_id,
          tenantName: tenantName.value[s.tenant_id] || '(unknown salon)',
          reason: s.reason,
          left: supportSession.remaining(s.expires_at, Date.now())
        };
      }).filter(function (s) { return !s.left.expired; });
    });

    return {
      admins: admins,
      log: log,
      liveSessions: liveSessions,
      loaded: loaded,
      email: email,
      note: note,
      add: add,
      remove: remove,
      me: api.auth,
      tenantName: tenantName,
      goTenant: nav.goTenant,
      stamp: store.stamp,
      ago: store.ago
    };
  },
  template: [
    '<div class="screen">',
    '  <div class="screen__head">',
    '    <div>',
    '      <h2>Platform admins</h2>',
    '      <p class="screen__lead">The named users. Being in this list is the whole of the',
    '        grant &mdash; every policy in the schema reads it, and taking a row away takes',
    '        away access to every salon at once.</p>',
    '    </div>',
    '  </div>',
    '',
    '  <p v-if="!loaded" class="empty">Loading…</p>',
    '',
    '  <table v-else-if="admins.length" class="grid grid--plain">',
    '    <thead><tr><th>Account</th><th>Note</th><th>Since</th><th></th></tr></thead>',
    '    <tbody>',
    '      <tr v-for="a in admins" :key="a.user_id">',
    '        <td>',
    '          <span class="grid__name">{{ a.email || a.user_id }}</span>',
    '          <span v-if="me.user && me.user.id === a.user_id" class="pill pill--ok">you</span>',
    '        </td>',
    '        <td>{{ a.note || "—" }}</td>',
    '        <td :title="stamp(a.created_at)">{{ ago(a.created_at) }}</td>',
    '        <td>',
    '          <confirm-button v-if="!me.user || me.user.id !== a.user_id"',
    '            label="Remove" size="sm" confirm="Remove their access"',
    '            @confirmed="remove(a)" />',
    '          <span v-else class="hint hint--inline">You cannot remove your own access.</span>',
    '        </td>',
    '      </tr>',
    '    </tbody>',
    '  </table>',
    '',
    '  <div class="card" :class="liveSessions.length ? \'card--warn\' : \'\'">',
    '    <h3>Support sessions right now</h3>',
    '    <p class="hint">An admin with a live session has a <strong>console</strong> that is',
    '      that salon&rsquo;s console. Anything they do in it is written as the salon &mdash; a',
    '      turn has no author &mdash; so this list, and the <code>support.start</code> entries in',
    '      the log below, are the whole of the record.</p>',
    '    <p v-if="!liveSessions.length" class="empty">Nobody is signed in as a salon.</p>',
    '    <ul v-else class="rows rows--tight">',
    '      <li v-for="s in liveSessions" :key="s.userId" class="row row--slim">',
    '        <span class="row__name">{{ s.email }}</span>',
    '        <span>as',
    '          <button type="button" class="link-btn" @click="goTenant(s.tenantId)">',
    '            {{ s.tenantName }}</button></span>',
    '        <span class="grid__sub">{{ s.reason || "no reason given" }}</span>',
    '        <span class="pill" :class="s.left.urgent ? \'pill--bad\' : \'pill--warn\'">',
    '          {{ s.left.label }} left</span>',
    '      </li>',
    '    </ul>',
    '  </div>',
    '',
    '  <form class="card" @submit.prevent="add">',
    '    <h3>Add an admin</h3>',
    '    <p class="hint">The account must already exist &mdash; make it under Authentication →',
    '      Users in the Supabase dashboard, or from any salon&rsquo;s People page. Adding it',
    '      here grants read and write across <strong>every</strong> tenant.</p>',
    '    <div class="card__row">',
    '      <input v-model="email" class="input" type="email" spellcheck="false"',
    '        placeholder="them@example.com" />',
    '      <input v-model="note" class="input" type="text" maxlength="120" placeholder="Note" />',
    '      <button type="submit" class="btn btn--primary">Add</button>',
    '    </div>',
    '  </form>',
    '',
    '  <h3 class="page__sub">Everything, everywhere</h3>',
    '  <p class="screen__lead">The last sixty admin actions across all salons. A tenant&rsquo;s',
    '    own page shows only its own.</p>',
    '',
    '  <table v-if="log.length" class="grid grid--plain">',
    '    <thead><tr><th>When</th><th>Who</th><th>Salon</th><th>Action</th></tr></thead>',
    '    <tbody>',
    '      <tr v-for="r in log" :key="r.id">',
    '        <td :title="stamp(r.at)">{{ ago(r.at) }}</td>',
    '        <td>{{ r.actor_email || "—" }}</td>',
    '        <td>',
    '          <button v-if="r.tenant_id && tenantName[r.tenant_id]" type="button"',
    '            class="link-btn" @click="goTenant(r.tenant_id)">{{ tenantName[r.tenant_id] }}</button>',
    '          <span v-else>{{ r.tenant_id ? "(deleted)" : "—" }}</span>',
    '        </td>',
    '        <td class="mono">{{ r.action }}</td>',
    '      </tr>',
    '    </tbody>',
    '  </table>',
    '</div>'
  ].join('\n')
};
